import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Search, Filter, ChevronRight, FileCheck, Shield, Zap } from 'lucide-react';
import { assessmentApi, queryKeys } from '../services/api';

const STATUS_CONFIG: Record<string, { label: string; color: string; icon: any }> = {
  SUBMITTED: { label: 'Chờ duyệt',    color: 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20', icon: Shield },
  AUDITING:  { label: 'Đang thẩm định', color: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20', icon: Zap },
  CONCLUDED: { label: 'Hoàn tất',     color: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20', icon: FileCheck },
};

export default function ChecklistsPage() {
  const [search, setSearch] = useState('');
  const [page]       = useState(1);

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.sessions.list({ search, page, type: 'checklists' }),
    queryFn:  () => assessmentApi.list({ page, limit: 20 }),
  });

  // Filter for checklists review (In a real app, this would be a server-side filter)
  const filteredData = data?.data?.filter(s => ['SUBMITTED', 'AUDITING', 'CONCLUDED'].includes(s.status)) || [];

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <h2 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight leading-tight font-heading">Duyệt checklist & đánh giá</h2>
          <p className="text-slate-500 dark:text-slate-400 mt-1 font-bold">Thẩm định hồ sơ tuân thủ và xác nhận kết quả kiểm toán</p>
        </div>
      </div>

      <div className="glass-panel p-8 border-slate-200 dark:border-white/5 bg-white dark:bg-white/[0.02] shadow-sm relative overflow-hidden">
        {/* Ambient Glow */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/5 blur-[100px] rounded-full pointer-events-none" />

        {/* Filters */}
        <div className="flex items-center gap-4 flex-wrap mb-8 relative z-10">
          <div className="flex items-center gap-3 bg-slate-50 dark:bg-black/40 border border-slate-200 dark:border-white/10 rounded-2xl px-5 py-3 flex-1 min-w-[300px] focus-within:border-indigo-500/50 transition-all shadow-inner">
            <Search size={18} className="text-slate-400" />
            <input 
              value={search} 
              onChange={e => setSearch(e.target.value)}
              placeholder="Tìm kiếm theo tên đơn vị hoặc mã hồ sơ..."
              className="text-sm outline-none flex-1 bg-transparent text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 font-bold"
            />
          </div>
          
          <button className="flex items-center gap-2 px-5 py-3 rounded-2xl bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 text-slate-600 dark:text-slate-400 text-xs font-black hover:bg-slate-200 dark:hover:bg-white/10 transition-all shadow-sm">
            <Filter size={14} /> Bộ lọc nâng cao
          </button>
        </div>

        {/* Table */}
        <div className="overflow-x-auto relative z-10">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-200 dark:border-white/10 text-left">
                {['Mã hồ sơ (Protocol)', 'Đơn vị & Tổ chức', 'Kỳ báo cáo', 'Trạng thái thẩm định', 'Tiến độ điểm số', ''].map(h => (
                  <th key={h} className="text-[10px] font-black text-slate-400 dark:text-slate-500 px-4 py-5 tracking-[0.1em] uppercase">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-white/5">
              {isLoading ? (
                <tr>
                  <td colSpan={6} className="text-center py-24 text-slate-400">
                    <div className="flex flex-col items-center gap-4">
                       <div className="w-10 h-10 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
                       <span className="text-[11px] font-black tracking-widest text-indigo-500/60 transition-pulse uppercase">Quy trình thẩm định đang tải...</span>
                    </div>
                  </td>
                </tr>
              ) : filteredData.length > 0 ? (
                filteredData.map(s => {
                  const cfg = STATUS_CONFIG[s.status] || STATUS_CONFIG.SUBMITTED;
                  return (
                    <tr key={s.id} className="group hover:bg-slate-50 dark:hover:bg-white/5 transition-all">
                      <td className="px-4 py-6">
                        <span className="font-mono text-[11px] font-black text-indigo-600 dark:text-indigo-400 tracking-tighter bg-indigo-500/5 px-2 py-1 rounded-lg border border-indigo-500/10">
                          {s.sessionCode}
                        </span>
                      </td>
                      <td className="px-4 py-6">
                        <p className="font-black text-slate-900 dark:text-white group-hover:text-indigo-600 dark:group-hover:text-indigo-300 transition-colors tracking-tight text-[13px]">{s.organization?.name ?? '—'}</p>
                        <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 mt-1 tracking-tight">{s.organization?.code}</p>
                      </td>
                      <td className="px-4 py-6 font-bold text-slate-600 dark:text-slate-400 whitespace-nowrap text-xs tracking-tight">
                        {s.period?.name ?? '—'}
                      </td>
                      <td className="px-4 py-6">
                        <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-xl text-[10px] font-black border tracking-tight shadow-sm ${cfg.color}`}>
                          <cfg.icon size={12} />
                          {cfg.label}
                        </span>
                      </td>
                      <td className="px-4 py-6">
                        <div className="flex items-center gap-3">
                          <div className="flex-1 h-1.5 bg-slate-100 dark:bg-white/5 rounded-full overflow-hidden border border-slate-200/30 dark:border-none w-24">
                            <div 
                              className="h-full bg-gradient-to-r from-indigo-500 to-indigo-600 shadow-[0_0_10px_rgba(99,102,241,0.3)] transition-all duration-1000" 
                              style={{ width: `${s.totalScore ?? 0}%` }}
                            />
                          </div>
                          <span className="font-black text-slate-900 dark:text-white text-sm tracking-tighter">
                            {s.totalScore ?? 0}<span className="text-[10px] text-slate-400 dark:text-slate-500 font-bold ml-0.5">/100</span>
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-6 text-right">
                        <Link 
                          to={`/assessments/${s.id}/checklist`} 
                          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-2xl bg-indigo-600 text-white hover:bg-indigo-500 transition-all text-[10px] font-black tracking-tight shadow-lg shadow-indigo-600/20 active:scale-95"
                        >
                          Duyệt checklist <ChevronRight size={14} />
                        </Link>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={6} className="text-center py-32 text-slate-400">
                     <div className="flex flex-col items-center gap-4 opacity-30">
                        <FileCheck size={48} />
                        <p className="text-xs font-black tracking-widest uppercase">Không có hồ sơ nào cần thẩm định</p>
                     </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
